import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Send, Phone } from "lucide-react";
import { SERVICES, CONTACT } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICE_OPTIONS = [...SERVICES.map((s) => s.name), "General Enquiry"];

const inputCls =
    "w-full border border-line bg-night/60 px-4 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none transition-colors duration-300 focus:border-accent";
const labelCls = "mb-2 block font-mono text-[0.65rem] uppercase tracking-[0.25em] text-slate-400";

export default function EnquiryForm({ preselected = "" }) {
    const validInitial = SERVICE_OPTIONS.includes(preselected) ? preselected : "";
    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: "",
        service: validInitial,
        message: "",
    });
    const [status, setStatus] = useState("idle");
    const [error, setError] = useState("");

    useEffect(() => {
        if (preselected && SERVICE_OPTIONS.includes(preselected)) {
            setForm((f) => ({ ...f, service: preselected }));
        }
    }, [preselected]);

    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        setError("");
        try {
            const res = await axios.post(`${API}/enquiries`, form);
            if (res.data && res.data.status === "success") {
                setStatus("success");
            } else {
                throw new Error("Unexpected response");
            }
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg =
                typeof detail === "string"
                    ? detail
                    : detail && detail[0] && detail[0].msg
                    ? `Please check your input: ${detail[0].msg}`
                    : "Something went wrong. Please try again or call us.";
            setStatus("error");
            setError(msg);
        }
    };

    const reset = () => {
        setForm({ name: "", phone: "", email: "", service: "", message: "" });
        setStatus("idle");
        setError("");
    };

    if (status === "success") {
        return (
            <motion.div
                data-testid="enquiry-success"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="flex h-full min-h-[420px] flex-col items-center justify-center border border-accent/40 bg-card p-10 text-center"
            >
                <CheckCircle2 className="h-14 w-14 text-accent" />
                <h3 className="mt-6 font-display text-3xl font-bold uppercase text-white">
                    Enquiry Received
                </h3>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-400">
                    Thank you. Our team has received your enquiry and will get back to you shortly.
                    For anything urgent, call us directly.
                </p>
                <a
                    href={CONTACT.phones[0].href}
                    data-testid="enquiry-success-call-btn"
                    className="mt-6 flex items-center gap-2 font-mono text-sm text-accent hover:underline"
                >
                    <Phone className="h-4 w-4" />
                    {CONTACT.phones[0].display}
                </a>
                <button
                    onClick={reset}
                    data-testid="enquiry-success-reset-btn"
                    className="mt-8 border border-line px-6 py-3 font-display text-xs font-bold uppercase tracking-widest text-slate-300 transition-colors hover:border-accent hover:text-accent"
                >
                    Send Another Enquiry
                </button>
            </motion.div>
        );
    }

    return (
        <form
            onSubmit={submit}
            data-testid="enquiry-form"
            className="border border-line bg-card p-6 sm:p-10"
        >
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-accent">
                Send an enquiry
            </p>
            <h3 className="mt-3 font-display text-3xl font-bold uppercase text-white">
                Tell us about your project
            </h3>
            <div className="mt-8 grid gap-6 sm:grid-cols-2">
                <div>
                    <label htmlFor="enq-name" className={labelCls}>
                        Name
                    </label>
                    <input
                        id="enq-name"
                        type="text"
                        required
                        minLength={2}
                        value={form.name}
                        onChange={set("name")}
                        data-testid="input-enquiry-name"
                        placeholder="Your full name"
                        className={inputCls}
                    />
                </div>
                <div>
                    <label htmlFor="enq-phone" className={labelCls}>
                        Phone Number
                    </label>
                    <input
                        id="enq-phone"
                        type="tel"
                        required
                        value={form.phone}
                        onChange={set("phone")}
                        data-testid="input-enquiry-phone"
                        placeholder="+91 00000 00000"
                        className={inputCls}
                    />
                </div>
                <div>
                    <label htmlFor="enq-email" className={labelCls}>
                        Email
                    </label>
                    <input
                        id="enq-email"
                        type="email"
                        required
                        value={form.email}
                        onChange={set("email")}
                        data-testid="input-enquiry-email"
                        placeholder="you@example.com"
                        className={inputCls}
                    />
                </div>
                <div>
                    <label htmlFor="enq-service" className={labelCls}>
                        Service Required
                    </label>
                    <select
                        id="enq-service"
                        required
                        value={form.service}
                        onChange={set("service")}
                        data-testid="select-enquiry-service"
                        className={`${inputCls} appearance-none ${form.service ? "" : "text-slate-500"}`}
                    >
                        <option value="" disabled>
                            Select a service
                        </option>
                        {SERVICE_OPTIONS.map((s) => (
                            <option key={s} value={s} className="bg-night text-white">
                                {s}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="sm:col-span-2">
                    <label htmlFor="enq-message" className={labelCls}>
                        Message
                    </label>
                    <textarea
                        id="enq-message"
                        required
                        minLength={5}
                        rows={5}
                        value={form.message}
                        onChange={set("message")}
                        data-testid="textarea-enquiry-message"
                        placeholder="Describe your site, requirement or the system you need..."
                        className={`${inputCls} resize-y`}
                    />
                </div>
            </div>
            {status === "error" && (
                <p
                    data-testid="enquiry-error"
                    className="mt-5 border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300"
                >
                    {error}
                </p>
            )}
            <button
                type="submit"
                disabled={status === "sending"}
                data-testid="enquiry-submit-btn"
                className="group mt-8 flex w-full items-center justify-center gap-3 bg-accent px-8 py-4 font-display text-base font-bold uppercase tracking-widest text-night transition-all duration-300 hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
                {status === "sending" ? (
                    <>
                        Sending
                        <Loader2 className="h-4 w-4 animate-spin" />
                    </>
                ) : (
                    <>
                        Submit Enquiry
                        <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </>
                )}
            </button>
        </form>
    );
}
